import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import Mug3DViewer, { getMaxPhotoWidth } from "../Mug3DViewer";
import "./CustomMug.css";

const CUSTOM_MUG_BASE_PRICE = 349;

const LAYOUT_OPTIONS = [
  { id: "classic", label: "Classic", hint: "One hero photo, elegant caption" },
  { id: "strip", label: "Photo Strip", hint: "Multiple photos side by side" },
  { id: "framed", label: "Framed", hint: "Decorative frame + ribbon banner" },
];

const CustomMug = () => {
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);

  const [customText, setCustomText] = useState("");
  const [textColor, setTextColor] = useState("#222222");
  const [photos, setPhotos] = useState([]); // Array of { id, url, width, height }
  const [mugLayout, setMugLayout] = useState("strip");
  const [loading, setLoading] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("Image size should be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const cap = getMaxPhotoWidth(mugLayout, photos.length + 1);
      const newPhoto = {
        id: Date.now(),
        url: reader.result,
        width: Math.min(280, cap), // Default width, capped for current layout/count
        height: 350, // Default height
      };
      setPhotos((prev) => [...prev, newPhoto]);
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateDimension = (id, field, value) => {
    setPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: Number(value) } : p))
    );
  };

  const handleRemovePhoto = (id) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  // Whenever the photo count or chosen layout changes, the per-photo width
  // budget shrinks or grows. Re-clamp existing widths to the new cap so the
  // 3D viewer's group-wide "fit the strip" rescale rarely has to engage —
  // that's what was making one photo's width slider visibly resize others.
  useEffect(() => {
    setPhotos((prev) => {
      const cap = getMaxPhotoWidth(mugLayout, prev.length);
      let changed = false;
      const next = prev.map((p) =>
        p.width > cap ? ((changed = true), { ...p, width: cap }) : p
      );
      return changed ? next : prev;
    });
  }, [mugLayout, photos.length]);

  const handleAddToCart = async () => {
    if (photos.length === 0 && !customText.trim()) {
      alert("Please upload at least one photo.");
      return;
    }

    setLoading(true);

    try {
      const customMugItem = {
        _id: `custom_mug_${Date.now()}`,
        name: "3D Custom Ceramic Mug with Sized Photos",
        price: CUSTOM_MUG_BASE_PRICE,
        image: photos[0]?.url || "https://via.placeholder.com/300x300?text=Custom+Mug",
        quantity: 1,
        isCustom: true,
        customization: {
          text: customText,
          textColor: textColor,
          photos: photos,
          layout: mugLayout,
        },
      };

      await addToCart(customMugItem);
      navigate("/cart");
    } catch (error) {
      console.error("Failed to add custom mug to cart", error);
    } finally {
      setLoading(false);
    }
  };

  const getImageLabel = (index) => {
    if (index === 0) return "1st Image";
    if (index === 1) return "2nd Image";
    if (index === 2) return "3rd Image";
    return `${index + 1}th Image`;
  };

  const activeTemplate = LAYOUT_OPTIONS.find((o) => o.id === mugLayout);
  const photoCap = mugLayout === "classic" ? 1 : mugLayout === "framed" ? 5 : 6;

  return (
    <div className="custom-mug-page">
      <div className="container">
        <div className="custom-header">
          <span className="badge">3D Photo Studio</span>
          <h1>Create Your Custom Photo Mug</h1>
          <p>Upload photos and adjust their individual width and height in real-time.</p>
        </div>

        <div className="custom-studio-grid">
          {/* LEFT: 3D Interactive Viewer */}
          <div className="preview-container">
            <Mug3DViewer
              photos={photos}
              customText={customText}
              textColor={textColor}
              layout={mugLayout}
            />
          </div>

          {/* RIGHT: Customization Controls */}
          <div className="controls-container">
            <div className="control-card">
              <h3>Choose a Design Layout</h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                }}
              >
                {LAYOUT_OPTIONS.map((opt) => {
                  const active = opt.id === mugLayout;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setMugLayout(opt.id)}
                      style={{
                        padding: "10px 8px",
                        borderRadius: "8px",
                        border: active ? "2px solid #9c6b3d" : "1px solid #ddd",
                        background: active ? "#fbf3ea" : "#fff",
                        color: "#333",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>{opt.label}</div>
                      <div style={{ fontSize: "0.7rem", color: "#777", marginTop: "2px" }}>
                        {opt.hint}
                      </div>
                    </button>
                  );
                })}
              </div>
              {activeTemplate && photos.length > photoCap && (
                <p style={{ fontSize: "0.75rem", color: "#b45309", marginTop: "8px" }}>
                  {activeTemplate.label} uses up to {photoCap} photo{photoCap > 1 ? "s" : ""};
                  extra photos won't be shown on the mug.
                </p>
              )}
            </div>

            <div className="control-card">
              <h3>Upload & Size Photos</h3>
              <input
                type="file"
                accept="image/*"
                id="mug-file-input"
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
              <label htmlFor="mug-file-input" className="upload-file-btn">
                <i className="fa-solid fa-cloud-arrow-up"></i> Add Photo ({photos.length} Added)
              </label>

              {/* List showing photos with Width and Height Sliders */}
              <div className="uploaded-photos-list" style={{ marginTop: "16px" }}>
                {photos.map((photo, index) => {
                  const maxWidth = getMaxPhotoWidth(mugLayout, photos.length);
                  return (
                  <div key={photo.id} className="photo-control-item" style={{ background: "#f9f9f9", padding: "12px", borderRadius: "8px", marginBottom: "12px", border: "1px solid #eee" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <img src={photo.url} alt={getImageLabel(index)} style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }} />
                        <span style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#333" }}>{getImageLabel(index)}</span>
                      </div>
                      <button type="button" onClick={() => handleRemovePhoto(photo.id)} style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer" }}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "8px" }}>
                      <div>
                        <label style={{ fontSize: "0.75rem", color: "#666" }}>
                          Width: {photo.width}px {maxWidth < 500 && `(max ${maxWidth}px with ${photos.length} photos)`}
                        </label>
                        <input
                          type="range"
                          min="60"
                          max={maxWidth}
                          value={Math.min(photo.width, maxWidth)}
                          onChange={(e) => handleUpdateDimension(photo.id, "width", e.target.value)}
                          style={{ width: "100%", cursor: "pointer", accentColor: "#9c6b3d" }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: "0.75rem", color: "#666" }}>Height: {photo.height}px</label>
                        <input
                          type="range"
                          min="100"
                          max="460"
                          value={photo.height}
                          onChange={(e) => handleUpdateDimension(photo.id, "height", e.target.value)}
                          style={{ width: "100%", cursor: "pointer", accentColor: "#9c6b3d" }}
                        />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="control-card">
              <h3>Add Custom Text / Name</h3>
              <input
                type="text"
                placeholder="e.g. Best Memories 🌟"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                maxLength={30}
                className="custom-text-input"
              />
              <div className="text-color-picker">
                <label>Text Color:</label>
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                />
              </div>
            </div>

            <div className="studio-footer-action">
              <div className="price-tag-box">
                <span>Price</span>
                <h2>₹{CUSTOM_MUG_BASE_PRICE}</h2>
              </div>
              <button
                className="add-to-cart-studio-btn"
                onClick={handleAddToCart}
                disabled={loading}
              >
                {loading ? "Adding..." : "Add to Cart"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomMug;